import React, { useState, useEffect, FC } from 'react';
import { useInView } from 'react-hook-inview';
import { useTranslation } from 'react-i18next';

import DotHover from '../../../images/solutions/dot-hover.svg';
import Dot from '../../../images/solutions/dot.svg';
import Bg from '../../../images/solutions/process-bg.png';
import ProcessFlow1 from '../../../images/solutions/process-flow-1-icon.svg';
import ProcessFlow2 from '../../../images/solutions/process-flow-2-icon.svg';
import ProcessFlow3 from '../../../images/solutions/process-flow-3-icon.svg';
import ProcessFlow4 from '../../../images/solutions/process-flow-4-icon.svg';
import ProcessFlow5 from '../../../images/solutions/process-flow-5-icon.svg';
import ProcessFlow6 from '../../../images/solutions/process-flow-6-icon.svg';
import {
  ProcessStyled,
  DiagonalGray,
  DiagonalBlue,
  ColumnLeft,
  WhiteBtn,
  InnerBgText,
  BlueBtn,
  BlueBtnRight,
  ButtonContainer,
  ColumnRight,
  ProcessCard,
  ImgCont,
  DotCont,
  BulletPlace,
  TextCont,
  Title,
  Subtitle,
  BulletContainer,
  TextContainer,
} from './style';

type TextBgType = {
  btnWhiteText1: string;
  btnWhiteIcon1: string;
  btnWhiteText2: string;
  btnWhiteIcon2: string;
  innerText: string;
  btnBlueText1: string;
  btnBlueText2: string;
};

interface Props {}

const Process: FC<Props> = () => {
  const { t } = useTranslation();
  const [targetRef, isInViewport] = useInView({
    unobserveOnEnter: true,
    threshold: 0.5,
  });
  const [activeProcess, setActiveProcess] = useState<number>(1);
  const [processChanged, setProcessChanged] = useState(false);
  const [activeProcessBgTexts, setActiveProcessBgTexts] = useState<TextBgType>({
    btnWhiteText1: t('SolutionProcessFlow1Title'),
    btnWhiteIcon1: ProcessFlow1,
    btnWhiteText2: t('SolutionProcessFlow2Title'),
    btnWhiteIcon2: ProcessFlow2,
    innerText: t('SolutionProcessWheelUnderstanding'),
    btnBlueText1: t('SolutionProcessWheelDeployment'),
    btnBlueText2: t('SolutionProcessWheelModeling'),
  });

  const horizontalScrollbar: HTMLElement | undefined | null =
    document.getElementById('inner');

  const onChangeProcess = (toActive: number) => {
    setActiveProcess(toActive);
    horizontalScrollbar!.scrollLeft = toActive === 1 ? 0 : 44 * toActive;
    if (toActive === 1 || toActive === 2)
      return setActiveProcessBgTexts({
        btnWhiteText1: t('SolutionProcessFlow1Title'),
        btnWhiteIcon1: ProcessFlow1,
        btnWhiteText2: t('SolutionProcessFlow2Title'),
        btnWhiteIcon2: ProcessFlow2,
        innerText: t('SolutionProcessWheelUnderstanding'),
        btnBlueText1: t('SolutionProcessWheelDeployment'),
        btnBlueText2: t('SolutionProcessWheelModeling'),
      });
    if (toActive === 3 || toActive === 4)
      return setActiveProcessBgTexts({
        btnWhiteText1: t('SolutionProcessFlow3Title'),
        btnWhiteIcon1: ProcessFlow3,
        btnWhiteText2: t('SolutionProcessFlow4Title'),
        btnWhiteIcon2: ProcessFlow4,
        innerText: t('SolutionProcessWheelModeling'),
        btnBlueText1: t('SolutionProcessWheelUnderstanding'),
        btnBlueText2: t('SolutionProcessWheelDeployment'),
      });
    if (toActive === 5 || toActive === 6)
      return setActiveProcessBgTexts({
        btnWhiteText1: t('SolutionProcessFlow5Title'),
        btnWhiteIcon1: ProcessFlow5,
        btnWhiteText2: t('SolutionProcessFlow6Title'),
        btnWhiteIcon2: ProcessFlow6,
        innerText: t('SolutionProcessWheelDeployment'),
        btnBlueText1: t('SolutionProcessWheelModeling'),
        btnBlueText2: t('SolutionProcessWheelUnderstanding'),
      });
  };

  useEffect(() => {
    if (isInViewport) {
      const interval = setInterval(() => {
        if (activeProcess < 6) {
          onChangeProcess(activeProcess + 1);
        }
        if (activeProcess === 6) {
          onChangeProcess(1);
        }
      }, 10000);
      if (activeProcess === 1 || activeProcess === 3 || activeProcess === 5) {
        setProcessChanged(true);
      } else {
        setProcessChanged(false);
      }
      return () => clearInterval(interval);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInViewport, activeProcess]);

  return (
    <ProcessStyled ref={targetRef} id="process">
      <DiagonalGray />
      <DiagonalBlue>
        <div className="container">
          <div className={isInViewport ? 'desktop' : 'desktop hide'}>
            <ColumnLeft>
              <Subtitle>{t('SolutionProcessSubtitle')}</Subtitle>
              <Title>{t('SolutionProcessTitle')}</Title>
              <ButtonContainer>
                <WhiteBtn
                  className={
                    processChanged
                      ? 'animate__animated animate__fadeIn animate__slower'
                      : ''
                  }
                >
                  <img src={activeProcessBgTexts.btnWhiteIcon1} alt="icon" />
                  <span>{activeProcessBgTexts.btnWhiteText1}</span>
                </WhiteBtn>
                <WhiteBtn
                  className={
                    processChanged
                      ? 'animate__animated animate__fadeIn animate__slower'
                      : ''
                  }
                >
                  <img src={activeProcessBgTexts.btnWhiteIcon2} alt="icon" />
                  <span>{activeProcessBgTexts.btnWhiteText2}</span>
                </WhiteBtn>
                <InnerBgText
                  className={
                    processChanged
                      ? 'animate__animated animate__fadeIn animate__slower'
                      : ''
                  }
                >
                  {activeProcessBgTexts.innerText}
                </InnerBgText>
                <BlueBtn
                  className={
                    processChanged
                      ? 'animate__animated animate__fadeIn animate__slower'
                      : ''
                  }
                >
                  {activeProcessBgTexts.btnBlueText1}
                </BlueBtn>
                <BlueBtnRight
                  className={
                    processChanged
                      ? 'animate__animated animate__fadeIn animate__slower'
                      : ''
                  }
                >
                  {activeProcessBgTexts.btnBlueText2}
                </BlueBtnRight>
              </ButtonContainer>
            </ColumnLeft>
            <ColumnRight>
              <ProcessCard
                className={activeProcess === 1 ? 'active-process' : ''}
                onClick={() => onChangeProcess(1)}
              >
                <BulletPlace>
                  <DotCont>
                    <img src={Dot} alt="dot" />
                    <img src={DotHover} alt="dot-hover" />
                  </DotCont>
                  <ImgCont
                    className={
                      activeProcess === 1
                        ? 'animate__animated animate__fadeIn animate__slower'
                        : ''
                    }
                  >
                    <img src={ProcessFlow1} alt="ProcessFlow1" />
                  </ImgCont>
                </BulletPlace>
                <TextCont>
                  <h6>{t('SolutionProcessFlow1Title')}</h6>
                  <p
                    className={
                      activeProcess === 1
                        ? 'animate__animated animate__fadeIn animate__slower'
                        : ''
                    }
                  >
                    {t('SolutionProcessFlow1Text')}
                  </p>
                </TextCont>
              </ProcessCard>
              <ProcessCard
                className={activeProcess === 2 ? 'active-process' : ''}
                onClick={() => onChangeProcess(2)}
              >
                <BulletPlace>
                  <DotCont>
                    <img src={Dot} alt="dot" />
                    <img src={DotHover} alt="dot-hover" />
                  </DotCont>
                  <ImgCont
                    className={
                      activeProcess === 2
                        ? 'animate__animated animate__fadeIn animate__slower'
                        : ''
                    }
                  >
                    <img src={ProcessFlow2} alt="ProcessFlow2" />
                  </ImgCont>
                </BulletPlace>
                <TextCont>
                  <h6>{t('SolutionProcessFlow2Title')}</h6>
                  <p
                    className={
                      activeProcess === 2
                        ? 'animate__animated animate__fadeIn animate__slower'
                        : ''
                    }
                  >
                    {t('SolutionProcessFlow2Text')}
                  </p>
                </TextCont>
              </ProcessCard>
              <ProcessCard
                className={activeProcess === 3 ? 'active-process' : ''}
                onClick={() => onChangeProcess(3)}
              >
                <BulletPlace>
                  <DotCont>
                    <img src={Dot} alt="dot" />
                    <img src={DotHover} alt="dot-hover" />
                  </DotCont>
                  <ImgCont
                    className={
                      activeProcess === 3
                        ? 'animate__animated animate__fadeIn animate__slower'
                        : ''
                    }
                  >
                    <img src={ProcessFlow3} alt="ProcessFlow3" />
                  </ImgCont>
                </BulletPlace>
                <TextCont>
                  <h6>{t('SolutionProcessFlow3Title')}</h6>
                  <p
                    className={
                      activeProcess === 3
                        ? 'animate__animated animate__fadeIn animate__slower'
                        : ''
                    }
                  >
                    {t('SolutionProcessFlow3Text')}
                  </p>
                </TextCont>
              </ProcessCard>
              <ProcessCard
                className={activeProcess === 4 ? 'active-process' : ''}
                onClick={() => onChangeProcess(4)}
              >
                <BulletPlace>
                  <DotCont>
                    <img src={Dot} alt="dot" />
                    <img src={DotHover} alt="dot-hover" />
                  </DotCont>
                  <ImgCont
                    className={
                      activeProcess === 4
                        ? 'animate__animated animate__fadeIn animate__slower'
                        : ''
                    }
                  >
                    <img src={ProcessFlow4} alt="ProcessFlow4" />
                  </ImgCont>
                </BulletPlace>
                <TextCont>
                  <h6>{t('SolutionProcessFlow4Title')}</h6>
                  <p
                    className={
                      activeProcess === 4
                        ? 'animate__animated animate__fadeIn animate__slower'
                        : ''
                    }
                  >
                    {t('SolutionProcessFlow4Text')}
                  </p>
                </TextCont>
              </ProcessCard>
              <ProcessCard
                className={activeProcess === 5 ? 'active-process' : ''}
                onClick={() => onChangeProcess(5)}
              >
                <BulletPlace>
                  <DotCont>
                    <img src={Dot} alt="dot" />
                    <img src={DotHover} alt="dot-hover" />
                  </DotCont>
                  <ImgCont
                    className={
                      activeProcess === 5
                        ? 'animate__animated animate__fadeIn animate__slower'
                        : ''
                    }
                  >
                    <img src={ProcessFlow5} alt="ProcessFlow5" />
                  </ImgCont>
                </BulletPlace>
                <TextCont>
                  <h6>{t('SolutionProcessFlow5Title')}</h6>
                  <p
                    className={
                      activeProcess === 5
                        ? 'animate__animated animate__fadeIn animate__slower'
                        : ''
                    }
                  >
                    {t('SolutionProcessFlow5Text')}
                  </p>
                </TextCont>
              </ProcessCard>
              <ProcessCard
                className={activeProcess === 6 ? 'active-process' : ''}
                onClick={() => onChangeProcess(6)}
              >
                <BulletPlace>
                  <DotCont>
                    <img src={Dot} alt="dot" />
                    <img src={DotHover} alt="dot-hover" />
                  </DotCont>
                  <ImgCont
                    className={
                      activeProcess === 6
                        ? 'animate__animated animate__fadeIn animate__slower'
                        : ''
                    }
                  >
                    <img src={ProcessFlow6} alt="ProcessFlow6" />
                  </ImgCont>
                </BulletPlace>
                <TextCont>
                  <h6>{t('SolutionProcessFlow6Title')}</h6>
                  <p
                    className={
                      activeProcess === 6
                        ? 'animate__animated animate__fadeIn animate__slower'
                        : ''
                    }
                  >
                    {t('SolutionProcessFlow6Text')}
                  </p>
                </TextCont>
              </ProcessCard>
            </ColumnRight>
            <img src={Bg} alt="bg" />
          </div>

          <div className="mobile">
            <Subtitle>{t('SolutionProcessSubtitle')}</Subtitle>

            <Title>{t('SolutionProcessTitle')}</Title>

            <BulletContainer currentProcess={activeProcess} id="inner">
              <div className="inner">
                <ProcessCard
                  className={activeProcess === 1 ? 'active-process' : ''}
                  onClick={() => onChangeProcess(1)}
                >
                  <BulletPlace>
                    <DotCont>
                      <img src={Dot} alt="dot" />
                      <img src={DotHover} alt="dot-hover" />
                    </DotCont>
                    <ImgCont
                      className={
                        activeProcess === 1
                          ? 'animate__animated animate__fadeIn animate__slower'
                          : ''
                      }
                    >
                      <img src={ProcessFlow1} alt="ProcessFlow1" />
                    </ImgCont>
                  </BulletPlace>
                </ProcessCard>
                <ProcessCard
                  className={activeProcess === 2 ? 'active-process' : ''}
                  onClick={() => onChangeProcess(2)}
                >
                  <BulletPlace>
                    <DotCont>
                      <img src={Dot} alt="dot" />
                      <img src={DotHover} alt="dot-hover" />
                    </DotCont>
                    <ImgCont
                      className={
                        activeProcess === 2
                          ? 'animate__animated animate__fadeIn animate__slower'
                          : ''
                      }
                    >
                      <img src={ProcessFlow2} alt="ProcessFlow2" />
                    </ImgCont>
                  </BulletPlace>
                </ProcessCard>
                <ProcessCard
                  className={activeProcess === 3 ? 'active-process' : ''}
                  onClick={() => onChangeProcess(3)}
                >
                  <BulletPlace>
                    <DotCont>
                      <img src={Dot} alt="dot" />
                      <img src={DotHover} alt="dot-hover" />
                    </DotCont>
                    <ImgCont
                      className={
                        activeProcess === 3
                          ? 'animate__animated animate__fadeIn animate__slower'
                          : ''
                      }
                    >
                      <img src={ProcessFlow3} alt="ProcessFlow3" />
                    </ImgCont>
                  </BulletPlace>
                </ProcessCard>
                <ProcessCard
                  className={activeProcess === 4 ? 'active-process' : ''}
                  onClick={() => onChangeProcess(4)}
                >
                  <BulletPlace>
                    <DotCont>
                      <img src={Dot} alt="dot" />
                      <img src={DotHover} alt="dot-hover" />
                    </DotCont>
                    <ImgCont
                      className={
                        activeProcess === 4
                          ? 'animate__animated animate__fadeIn animate__slower'
                          : ''
                      }
                    >
                      <img src={ProcessFlow4} alt="ProcessFlow4" />
                    </ImgCont>
                  </BulletPlace>
                </ProcessCard>
                <ProcessCard
                  className={activeProcess === 5 ? 'active-process' : ''}
                  onClick={() => onChangeProcess(5)}
                >
                  <BulletPlace>
                    <DotCont>
                      <img src={Dot} alt="dot" />
                      <img src={DotHover} alt="dot-hover" />
                    </DotCont>
                    <ImgCont
                      className={
                        activeProcess === 5
                          ? 'animate__animated animate__fadeIn animate__slower'
                          : ''
                      }
                    >
                      <img src={ProcessFlow5} alt="ProcessFlow5" />
                    </ImgCont>
                  </BulletPlace>
                </ProcessCard>
                <ProcessCard
                  className={activeProcess === 6 ? 'active-process' : ''}
                  onClick={() => onChangeProcess(6)}
                >
                  <BulletPlace>
                    <DotCont>
                      <img src={Dot} alt="dot" />
                      <img src={DotHover} alt="dot-hover" />
                    </DotCont>
                    <ImgCont
                      className={
                        activeProcess === 6
                          ? 'animate__animated animate__fadeIn animate__slower'
                          : ''
                      }
                    >
                      <img src={ProcessFlow6} alt="ProcessFlow6" />
                    </ImgCont>
                  </BulletPlace>
                </ProcessCard>
              </div>
            </BulletContainer>

            <TextContainer>
              <ProcessCard
                className={activeProcess === 1 ? 'active-process' : ''}
              >
                <TextCont>
                  <h6>{t('SolutionProcessFlow1Title')}</h6>
                  <p
                    className={
                      activeProcess === 1
                        ? 'animate__animated animate__fadeIn animate__slower'
                        : ''
                    }
                  >
                    {t('SolutionProcessFlow1Text')}
                  </p>
                </TextCont>
              </ProcessCard>
              <ProcessCard
                className={activeProcess === 2 ? 'active-process' : ''}
              >
                <TextCont>
                  <h6>{t('SolutionProcessFlow2Title')}</h6>
                  <p
                    className={
                      activeProcess === 2
                        ? 'animate__animated animate__fadeIn animate__slower'
                        : ''
                    }
                  >
                    {t('SolutionProcessFlow2Text')}
                  </p>
                </TextCont>
              </ProcessCard>
              <ProcessCard
                className={activeProcess === 3 ? 'active-process' : ''}
              >
                <TextCont>
                  <h6>{t('SolutionProcessFlow3Title')}</h6>
                  <p
                    className={
                      activeProcess === 3
                        ? 'animate__animated animate__fadeIn animate__slower'
                        : ''
                    }
                  >
                    {t('SolutionProcessFlow3Text')}
                  </p>
                </TextCont>
              </ProcessCard>
              <ProcessCard
                className={activeProcess === 4 ? 'active-process' : ''}
              >
                <TextCont>
                  <h6>{t('SolutionProcessFlow4Title')}</h6>
                  <p
                    className={
                      activeProcess === 4
                        ? 'animate__animated animate__fadeIn animate__slower'
                        : ''
                    }
                  >
                    {t('SolutionProcessFlow4Text')}
                  </p>
                </TextCont>
              </ProcessCard>
              <ProcessCard
                className={activeProcess === 5 ? 'active-process' : ''}
              >
                <TextCont>
                  <h6>{t('SolutionProcessFlow5Title')}</h6>
                  <p
                    className={
                      activeProcess === 5
                        ? 'animate__animated animate__fadeIn animate__slower'
                        : ''
                    }
                  >
                    {t('SolutionProcessFlow5Text')}
                  </p>
                </TextCont>
              </ProcessCard>
              <ProcessCard
                className={activeProcess === 6 ? 'active-process' : ''}
              >
                <TextCont>
                  <h6>{t('SolutionProcessFlow6Title')}</h6>
                  <p
                    className={
                      activeProcess === 6
                        ? 'animate__animated animate__fadeIn animate__slower'
                        : ''
                    }
                  >
                    {t('SolutionProcessFlow6Text')}
                  </p>
                </TextCont>
              </ProcessCard>
            </TextContainer>

            <ButtonContainer>
              <WhiteBtn
                className={
                  processChanged
                    ? 'animate__animated animate__fadeIn animate__slower'
                    : ''
                }
              >
                <img src={activeProcessBgTexts.btnWhiteIcon1} alt="icon" />
                <span>{activeProcessBgTexts.btnWhiteText1}</span>
              </WhiteBtn>
              <WhiteBtn
                className={
                  processChanged
                    ? 'animate__animated animate__fadeIn animate__slower'
                    : ''
                }
              >
                <img src={activeProcessBgTexts.btnWhiteIcon2} alt="icon" />
                <span>{activeProcessBgTexts.btnWhiteText2}</span>
              </WhiteBtn>
              <InnerBgText
                className={
                  processChanged
                    ? 'animate__animated animate__fadeIn animate__slower'
                    : ''
                }
              >
                {activeProcessBgTexts.innerText}
              </InnerBgText>
            </ButtonContainer>
          </div>
        </div>
      </DiagonalBlue>
    </ProcessStyled>
  );
};

export default Process;
